import useWidth from '@components/Hooks/useWidth';
import { useState } from 'react';
import useSearchParams from '../../../../../hooks/useSearchParams';
import { useTabSearchParam } from '../../../../../hooks/useTabSearchParam';
import { ClarityEvents } from '../../../../../lib/clarity/types/clarityEvents.types';
import { captureEvent } from '../../../../../lib/clarity/utils/captureEvent.utils';
import { ObjectScreenContextValue } from '../contexts/ObjectScreen.context';
import { OBJECT_SCREEN_TAB_SEARCH_PARAM } from '../objectScreen.constants';

interface UseObjectScreenStateParams {
  objectId: string;
  objectType: string;
  defaultTab?: string;
  isDetailsOpen?: boolean;
  defaultDetailsOpen?: boolean;
  hasTabScopedFilters?: boolean;
  onDetailsOpenChange?: (isOpen: boolean) => void;
}

export function useObjectScreenState({
  objectId,
  objectType,
  defaultTab,
  isDetailsOpen: controlledDetailsOpen,
  defaultDetailsOpen,
  hasTabScopedFilters = false,
  onDetailsOpenChange,
}: UseObjectScreenStateParams) {
  const { isCustomizedTabletDown } = useWidth();
  const defaultTabKey = defaultTab ?? '';
  const [activeTab, setTabSearchParam] = useTabSearchParam(
    OBJECT_SCREEN_TAB_SEARCH_PARAM,
    defaultTabKey,
  );
  const { searchParams, setSearchParams } = useSearchParams();
  const [controlsNode, setControlsNode] = useState<HTMLDivElement | null>(null);
  const [filterBarNode, setFilterBarNode] = useState<HTMLDivElement | null>(null);
  const [scrollParent, setScrollParent] = useState<HTMLDivElement | null>(null);
  const [stickyToolbar, setStickyToolbar] = useState<HTMLDivElement | null>(null);
  const [stickyFilterBar, setStickyFilterBar] = useState<HTMLDivElement | null>(null);
  const [detailsExpansionSteps, setDetailsExpansionSteps] = useState(0);
  const [internalDetailsOpen, setInternalDetailsOpen] = useState(
    defaultDetailsOpen ?? !isCustomizedTabletDown,
  );

  const isDetailsControlled = controlledDetailsOpen !== undefined;
  const isDetailsOpen = isDetailsControlled ? controlledDetailsOpen : internalDetailsOpen;

  const setActiveTab = (tabKey: string) => {
    if (!hasTabScopedFilters) {
      setTabSearchParam(tabKey);
      return;
    }

    if (tabKey === activeTab) {
      return;
    }

    const clearedParams = Object.fromEntries(
      Object.keys(searchParams).map((paramKey) => [paramKey, undefined]),
    );

    setSearchParams({
      ...clearedParams,
      [OBJECT_SCREEN_TAB_SEARCH_PARAM]: tabKey === defaultTabKey ? undefined : tabKey,
    });
  };

  const toggleDetails = () => {
    const next = !isDetailsOpen;
    captureEvent(
      next
        ? ClarityEvents.OBJECT_SCREEN_OPENED_DETAILS_PANEL
        : ClarityEvents.OBJECT_SCREEN_CLOSED_DETAILS_PANEL,
    );
    if (!isDetailsControlled) {
      setInternalDetailsOpen(next);
    }
    onDetailsOpenChange?.(next);
  };

  const contextValue: ObjectScreenContextValue = {
    objectId,
    objectType,
    activeTab,
    setActiveTab,
    regionTargets: {
      controls: controlsNode,
      filterBar: filterBarNode,
    },
    scrollParent,
    stickyToolbar,
    stickyFilterBar,
    isDetailsOpen,
    detailsExpansionSteps: isDetailsOpen ? detailsExpansionSteps : 0,
  };

  return {
    contextValue,
    scrollParent,
    stickyToolbar,
    setControlsNode,
    setFilterBarNode,
    setScrollParent,
    setStickyToolbar,
    setStickyFilterBar,
    isDetailsOpen,
    toggleDetails,
    setDetailsExpansionSteps,
  };
}
